import type { BriefAttachment, BusinessType, SubdomainCheck } from "../../../lib/api";
import { BRIEF_LIMITS, BriefMediaSchema, InspirationUrlSchema, briefValidationIssues } from "@prototypical/types";
import { FIX_STEP_LABELS, validOptionalEmail, type FixStep } from "../shared/startProblem";
import { BUSINESS_TYPES, CAPABILITIES, DAYS_OF_WEEK, integrationsForType, type Hours } from "../../../lib/catalog";
import { CUSTOMER_ROOT_DOMAIN } from "../../../lib/domains";
import { PHOTO_ACCEPT } from "../../../lib/imageTypes";
import { ErrBox, NavButtons, SubdomainHint, normalizeUrl } from "../shared";
import { DescriptionSuggestions } from "./components/DescriptionSuggestions";
import { InspoPreviewCard, type InspoSlot } from "./components/InspoPreviewCard";
import { ReviewBlock } from "./components/ReviewBlock";

export function StepAbout({ type, setType, canStep1, setStep }: {
  type: BusinessType | "";
  setType: (t: BusinessType) => void;
  canStep1: boolean;
  setStep: (s: 2) => void;
}) {
  return (
<div className="stack" style={{ gap: 16 }}>
          <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
            What kind of business is this?
          </label>
          <div className="stack" style={{ gap: 6 }}>
            {BUSINESS_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                className={`type-card${type === t.value ? " selected" : ""}`}
              >
                <div style={{ fontWeight: 600 }}>{t.label}</div>
                {t.example && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Examples: {t.example}</div>}
              </button>
            ))}
          </div>
          <NavButtons onNext={() => setStep(2)} nextDisabled={!canStep1} />
        </div>
  );
}

export function StepBasics(props: {
  type: BusinessType | "";
  name: string; setName: (v: string) => void;
  tagline: string; setTagline: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  onboardingError: string | null;
  canStep2: boolean;
  setStep: (s: 1 | 3) => void;
}) {
  const { type, name, setName, tagline, setTagline, description, setDescription, onboardingError, canStep2, setStep } = props;
  return (
<div className="stack" style={{ gap: 16 }}>
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Business name
            </label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Asheville Pizza Kitchen"
              style={{ marginTop: 6 }}
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              One-line tagline <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="e.g. Family-owned pizzeria since 1998"
              style={{ marginTop: 6 }}
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Tell us about your business
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What you do, who you serve, what makes you you. The more detail the better — this is what we'll write the site copy from."
              rows={5}
              style={{ marginTop: 6, minHeight: 120, resize: "vertical" }}
            />
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {description.trim().length} / 20 characters minimum
            </div>
            {onboardingError && <ErrBox>{onboardingError}</ErrBox>}
          </div>

          <DescriptionSuggestions
            type={type as BusinessType}
            name={name}
            tagline={tagline}
            onPick={setDescription}
          />

          <NavButtons onBack={() => setStep(1)} onNext={() => setStep(3)} nextDisabled={!canStep2} />
        </div>
  );
}

export function StepContact(props: {
  address: string; setAddress: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  hours: Hours; setHour: (day: string, patch: Partial<Hours[string]>) => void;
  canStep3: boolean;
  setStep: (s: 2 | 4) => void;
}) {
  const { address, setAddress, phone, setPhone, email, setEmail, hours, setHour, canStep3, setStep } = props;
  return (
<div className="stack" style={{ gap: 18 }}>
          <div className="muted" style={{ fontSize: 13 }}>
            All optional — fill in what you have. Hours show up as a section on the site.
          </div>
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>Address</label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Street, city, state"
              style={{ marginTop: 6 }}
            />
          </div>
          <div className="row" style={{ gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>Phone</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                style={{ marginTop: 6 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>Email</label>
              <input
                type="email"
                aria-label="Email"
                aria-invalid={!validOptionalEmail(email)}
                aria-describedby={!validOptionalEmail(email) ? "contact-email-error" : undefined}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@business.com"
                style={{ marginTop: 6 }}
              />
              {!validOptionalEmail(email) && <p id="contact-email-error" role="alert">Enter a valid email address, or leave Email blank.</p>}
            </div>
          </div>
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: "block" }}>
              Hours
            </label>
            <div className="stack" style={{ gap: 6 }}>
              {DAYS_OF_WEEK.map((d) => {
                const h = hours[d.id];
                return (
                  <div key={d.id} className="hours-row">
                    <div className="hours-day">{d.label}</div>
                    <label className="hours-toggle">
                      <input
                        type="checkbox"
                        checked={!h.closed}
                        onChange={(e) => setHour(d.id, { closed: !e.target.checked })}
                      />
                      <span className="muted">{h.closed ? "closed" : "open"}</span>
                    </label>
                    <div className="hours-times">
                      <input
                        type="time"
                        value={h.open}
                        onChange={(e) => setHour(d.id, { open: e.target.value })}
                        disabled={h.closed}
                      />
                      <span className="muted" style={{ fontSize: 12 }}>to</span>
                      <input
                        type="time"
                        value={h.close}
                        onChange={(e) => setHour(d.id, { close: e.target.value })}
                        disabled={h.closed}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <NavButtons onBack={() => setStep(2)} onNext={() => setStep(4)} nextDisabled={!canStep3} />
        </div>
  );
}

export function StepCapabilities({ capIds, toggleCap, canStep4, setStep }: {
  capIds: string[];
  toggleCap: (id: string) => void;
  canStep4: boolean;
  setStep: (s: 3 | 5) => void;
}) {
  return (
<div className="stack" style={{ gap: 16 }}>
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              What does the site need to do?
            </label>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Pick everything that applies. We'll add the right sections + pages.
            </div>
          </div>
          <div className="stack" style={{ gap: 6 }}>
            {CAPABILITIES.map((c) => {
              const on = capIds.includes(c.id);
              const locked = !!c.alwaysOn;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCap(c.id)}
                  className={`cap-card${on ? " selected" : ""}${locked ? " locked" : ""}`}
                  disabled={locked}
                >
                  <div className="row between">
                    <div>
                      <div style={{ fontWeight: 600 }}>{c.label}</div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{c.note}</div>
                    </div>
                    <div className="cap-check">{on ? "✓" : ""}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <NavButtons onBack={() => setStep(3)} onNext={() => setStep(5)} nextDisabled={!canStep4} />
        </div>
  );
}

export function StepIntegrations({ type, integrationIds, toggleIntegration, canStep5, setStep }: {
  type: BusinessType | "";
  integrationIds: string[];
  toggleIntegration: (id: string) => void;
  canStep5: boolean;
  setStep: (s: 4 | 6) => void;
}) {
  const eligibleIntegrations = type ? integrationsForType(type as BusinessType) : [];
  return (
<div className="stack" style={{ gap: 16 }}>
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Any tools you already use?
            </label>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Optional. Pick anything you want connected — we'll prompt for the actual handles/IDs after the site is up.
            </div>
          </div>
          {eligibleIntegrations.length === 0 ? (
            <div className="muted" style={{ fontStyle: "italic" }}>
              No specific integrations for this business type. You can add custom links later.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 8 }}>
              {eligibleIntegrations.map((i) => {
                const on = integrationIds.includes(i.id);
                return (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => toggleIntegration(i.id)}
                    className={`integ-card${on ? " selected" : ""}`}
                  >
                    <div style={{ fontWeight: 600 }}>{i.label}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{i.note}</div>
                  </button>
                );
              })}
            </div>
          )}
          <NavButtons onBack={() => setStep(4)} onNext={() => setStep(6)} nextDisabled={!canStep5} />
        </div>
  );
}

export function StepInspiration(props: {
  motion: boolean; setMotion: React.Dispatch<React.SetStateAction<boolean>>;
  inspoUrls: string[]; inspoPreviews: InspoSlot[];
  setInspo: (idx: number, val: string) => void;
  addInspoRow: () => void; removeInspoRow: (idx: number) => void;
  canStep6: boolean; setStep: (s: 5 | 7) => void;
}) {
  const {
    motion, setMotion, inspoUrls, inspoPreviews,
    setInspo, addInspoRow, removeInspoRow, canStep6, setStep,
  } = props;
  return (
<div className="stack" style={{ gap: 18 }}>
          <div className="row between" style={{ alignItems: "flex-start", gap: 12 }}>
            <div className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
              <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
                Allow animation
              </label>
              <div className="muted" style={{ fontSize: 12 }}>
                Allow animated transitions and interactive motion. Visitors’
                reduced-motion preferences are always respected.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={motion}
              onClick={() => setMotion((m) => !m)}
              className={`toggle-switch${motion ? " on" : ""}`}
              aria-label={motion ? "Motion on" : "Motion off"}
            >
              <span className="toggle-knob" aria-hidden />
            </button>
          </div>

          {/* hairline divider */}
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Sites you like
            </label>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Optional. Share references to help explain your needs. Your site will have its own design and content.
              Drop in as many as feel useful.
            </div>
          </div>
          <div className="stack" style={{ gap: 14 }}>
            {inspoUrls.map((u, i) => {
              const slot = inspoPreviews[i] ?? { state: "idle" as const };
              const invalid = !!u.trim() && !InspirationUrlSchema.safeParse(u).success;
              const showPreview = slot.state !== "idle";
              return (
                <div
                  key={i}
                  className={`inspo-row${showPreview ? " has-preview" : ""}`}
                >
                  <div className="inspo-input-row">
                    <span className="inspo-row-icon" aria-hidden>🔗</span>
                    <input
                      aria-label={`Inspiration link ${i + 1}`}
                      aria-invalid={invalid}
                      aria-describedby={invalid ? `inspo-error-${i}` : undefined}
                      value={u}
                      onChange={(e) => setInspo(i, e.target.value)}
                      placeholder={i === 0 ? "e.g. apple.com" : "Add another URL"}
                      className="inspo-input"
                      autoComplete="url"
                    />
                    {(inspoUrls.length > 1 || u.trim()) && (
                      <button
                        type="button"
                        onClick={() => removeInspoRow(i)}
                        className="inspo-remove"
                        aria-label="Remove URL"
                        title="Remove"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {invalid && <p id={`inspo-error-${i}`} role="alert">Enter a website address such as example.com, or remove this link.</p>}
                  <InspoPreviewCard slot={slot} />
                </div>
              );
            })}
            {inspoUrls.length < 8 && (
              <button
                type="button"
                onClick={addInspoRow}
                className="inspo-add"
              >
                + Add another inspiration site
              </button>
            )}
          </div>
          <NavButtons onBack={() => setStep(5)} onNext={() => setStep(7)} nextDisabled={!canStep6} />
        </div>
  );
}

export function StepMedia(props: {
  ownerNote: string; setOwnerNote: (v: string) => void;
  attachments: BriefAttachment[];
  logoPreview: string | null; setLogoPreview: (v: string | null) => void;
  uploading: string | null; uploadProgress: number | null; uploadErr: string | null;
  dismissUploadError: () => void;
  zipSkipped: Array<{ name: string; reason: string }>;
  logoInputRef: React.RefObject<HTMLInputElement>;
  photosInputRef: React.RefObject<HTMLInputElement>;
  zipInputRef: React.RefObject<HTMLInputElement>;
  handleLogoPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePhotosPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleZipPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeAttachment: (gcsPath?: string) => void;
  canStep7: boolean; setStep: (s: 6 | 8) => void;
}) {
  const {
    ownerNote, setOwnerNote, attachments, logoPreview, setLogoPreview,
    uploading, uploadProgress, uploadErr, dismissUploadError, zipSkipped,
    logoInputRef, photosInputRef, zipInputRef,
    handleLogoPick, handlePhotosPick, handleZipPick, removeAttachment,
    canStep7, setStep,
  } = props;
  const media = BriefMediaSchema.safeParse({ attachments, ownerNote });
  return (
<div className="stack" style={{ gap: 18 }}>
          {/* Logo upload */}
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Site logo <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              PNG or SVG works best. We'll size + recolor as needed.
            </div>
            <div className="logo-uploader" style={{ marginTop: 8 }}>
              <div className="logo-preview">
                {logoPreview ? (
                  <img src={logoPreview} alt="logo preview" />
                ) : (
                  <span className="muted" style={{ fontSize: 12 }}>no logo yet</span>
                )}
              </div>
              <div className="stack" style={{ gap: 6, flex: 1 }}>
                <input
                  ref={logoInputRef}
                  aria-label="Choose logo file"
                  type="file"
                  accept="image/png,image/svg+xml,image/jpeg,image/webp"
                  onChange={handleLogoPick}
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  className="btn"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={!!uploading}
                  style={{ alignSelf: "flex-start" }}
                >
                  {logoPreview ? "Change logo" : "Upload logo"}
                </button>
                {logoPreview && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      const logo = attachments.find((a) => a.tag === "logo");
                      if (logo) removeAttachment(logo.gcsPath);
                      setLogoPreview(null);
                    }}
                    style={{ alignSelf: "flex-start", color: "#b91c1c", borderColor: "#fecaca" }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Photos upload */}
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Photos <span style={{ fontWeight: 400 }}>(optional, up to 25MB each)</span>
            </label>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Anything you want on the site — your space, your work, your team.
            </div>
            <input
              ref={photosInputRef}
              aria-label="Choose photo files"
              type="file"
              accept={PHOTO_ACCEPT}
              multiple
              onChange={handlePhotosPick}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className="btn"
              onClick={() => photosInputRef.current?.click()}
              disabled={!!uploading}
              style={{ marginTop: 8, alignSelf: "flex-start" }}
            >
              + Add photos
            </button>
            {attachments.filter((a) => a.tag !== "logo").length > 0 && (
              <div className="stack" style={{ gap: 4, marginTop: 8 }}>
                <div className="muted" style={{ fontSize: 12 }}>Added files — photos, documents, and other uploads</div>
                {attachments.filter((a) => a.tag !== "logo").map((a) => (
                  <div key={a.gcsPath} className="row between" style={{
                    padding: "6px 10px",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    fontSize: 13,
                  }}>
                    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{a.name} <span className="muted">({Math.round(a.sizeBytes / 1024)} KB)</span></span>
                    <button
                      type="button"
                      aria-label={`Remove ${a.name}`}
                      onClick={() => removeAttachment(a.gcsPath)}
                      style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", fontSize: 13 }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bulk upload from a folder (.zip) */}
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Have a folder? Drop a .zip <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              We'll unpack it and sort: <code>logo.*</code> becomes your logo,
              other images go to photos, PDFs and text/markdown become docs the
              agent reads as content. Up to 1 GB.
            </div>
            <details style={{ marginTop: 8, fontSize: 12 }}>
              <summary>How to create a ZIP</summary>
              <p>Windows: select your files or folder, right-click, then choose Compress to ZIP file (or Send to → Compressed (zipped) folder).</p>
              <p>Mac: select your files or folder in Finder, right-click, then choose Compress.</p>
              <p>iPhone or iPad: save files in the Files app, select them, then choose More → Compress.</p>
            </details>
            <input
              ref={zipInputRef}
              aria-label="Choose ZIP file"
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              onChange={handleZipPick}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className="btn"
              onClick={() => zipInputRef.current?.click()}
              disabled={!!uploading}
              style={{ marginTop: 8, alignSelf: "flex-start" }}
            >
              + Upload a folder (.zip)
            </button>
            {zipSkipped.length > 0 && (
              <details style={{ marginTop: 8, fontSize: 12 }}>
                <summary className="muted" style={{ cursor: "pointer" }}>
                  Skipped {zipSkipped.length} file{zipSkipped.length === 1 ? "" : "s"}
                </summary>
                <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                  {zipSkipped.slice(0, 50).map((s, i) => (
                    <li key={i} className="muted">
                      <code>{s.name}</code> — {s.reason.replace(/_/g, " ")}
                    </li>
                  ))}
                  {zipSkipped.length > 50 && (
                    <li className="muted">…and {zipSkipped.length - 50} more</li>
                  )}
                </ul>
              </details>
            )}
          </div>

          {uploading && (
            <div className="muted" style={{ fontSize: 12 }}>
              {uploadProgress !== null
                ? `Uploading ${uploading}… ${uploadProgress}%`
                : `Uploading ${uploading}…`}
            </div>
          )}
          {uploadErr && (
            <div role="alert" style={{
              padding: 10,
              background: "#fee2e2",
              border: "1px solid #fecaca",
              borderRadius: 6,
              color: "#991b1b",
              fontSize: 13,
            }}>
              <div style={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{uploadErr}</div>
              <p>Your added files are safe. Select the failed files again to retry, or continue using only the files already added.</p>
              <button type="button" className="btn" disabled={!!uploading} onClick={dismissUploadError}>Continue without failed files</button>
            </div>
          )}

          {/* Owner note */}
          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Anything else we should know?
            </label>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Optional. Voice, must-haves, brand notes — anything the agent should keep in mind.
            </div>
            <textarea
              value={ownerNote}
              onChange={(e) => setOwnerNote(e.target.value)}
              placeholder="e.g. We're family-friendly but not childish. Keep the food photography front and center."
              rows={4}
              style={{ marginTop: 6, minHeight: 100, resize: "vertical" }}
              maxLength={BRIEF_LIMITS.ownerNote}
            />
          </div>

          <p className="muted">{attachments.length} of {BRIEF_LIMITS.attachments} files added.</p>
          {!media.success && <ErrBox>{[...new Set(briefValidationIssues(media.error.issues).map(issue => issue.message))].join(" ")}</ErrBox>}
          <NavButtons onBack={() => setStep(6)} onNext={() => setStep(8)} nextDisabled={!canStep7 || !!uploading} nextLabel="Review →" />
        </div>
  );
}

export function StepReview(props: {
  type: BusinessType | "";
  name: string; tagline: string; description: string;
  address: string; phone: string; email: string; hours: Hours;
  capIds: string[]; integrationIds: string[]; inspoUrls: string[];
  ownerNote: string; attachments: BriefAttachment[]; logoPreview: string | null;
  subdomain: string; setSubdomain: (v: string) => void; setSubdomainTouched: (v: boolean) => void;
  subCheck: SubdomainCheck | null;
  submitting: boolean; submitErr: string | null;
  submitFixStep?: FixStep;
  canSubmit: boolean; handleSubmit: () => void; setStep: (s: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8) => void;
}) {
  const {
    type, name, tagline, description, address, phone, email, hours,
    capIds, integrationIds, inspoUrls, ownerNote, attachments, logoPreview,
    subdomain, setSubdomain, setSubdomainTouched, subCheck,
    submitting, submitErr, submitFixStep, canSubmit, handleSubmit, setStep,
  } = props;
  return (
<div className="stack" style={{ gap: 18 }}>
          <ReviewBlock
            type={type as BusinessType}
            name={name}
            tagline={tagline}
            description={description}
            address={address}
            phone={phone}
            email={email}
            hours={hours}
            capIds={capIds}
            integrationIds={integrationIds}
            inspoUrls={inspoUrls.map(normalizeUrl).filter(Boolean) as string[]}
            ownerNote={ownerNote}
            attachments={attachments}
            logoPreview={logoPreview}
            onEdit={(s) => setStep(s as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8)}
          />

          <div>
            <label className="muted" style={{ fontSize: 13, fontWeight: 600 }}>
              Pick your protopal.ai address
            </label>
            <div className="row" style={{ marginTop: 6, gap: 0 }}>
              <input
                value={subdomain}
                onChange={(e) => { setSubdomain(e.target.value.toLowerCase()); setSubdomainTouched(true); }}
                placeholder="your-business"
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              />
              <span style={{
                padding: "8px 12px",
                border: "1px solid var(--border)",
                borderLeft: "none",
                borderTopRightRadius: "var(--radius)",
                borderBottomRightRadius: "var(--radius)",
                background: "#f9fafb",
                color: "var(--muted)",
                whiteSpace: "nowrap",
              }}>.{CUSTOMER_ROOT_DOMAIN}</span>
            </div>
            <SubdomainHint check={subCheck} subdomain={subdomain} />
          </div>

          <div className="row" style={{ gap: 10 }}>
            <button className="btn primary" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? "Starting your site…" : "Start building →"}
            </button>
            <button type="button" className="btn" onClick={() => setStep(7)} disabled={submitting}>
              ← Back
            </button>
          </div>
          {submitErr && <ErrBox>{submitErr}{submitFixStep && <div style={{ marginTop: 8 }}><button type="button" className="btn" onClick={() => setStep(submitFixStep)}>Edit {FIX_STEP_LABELS[submitFixStep]}</button></div>}</ErrBox>}
          <div className="muted" style={{ fontSize: 12 }}>
            We'll build your draft in the background and show you its progress. You can review and edit it before publishing.
          </div>
        </div>
  );
}
